import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "../lib/api";
import { trackSiteEvent } from "../lib/siteTrack";
import { externalLinkClickHandler } from "../lib/openExternal";
import { useAuth } from "../context/AuthContext";

/**
 * AppDownloadRibbon — v145.17
 *
 * Fixed-top ribbon nudging Android / iOS visitors to install the
 * native Linkola app. Super Admin manages URLs in `/admin/settings`;
 * empty URL for a platform = ribbon hidden on that platform.
 *
 * v145.17 changes
 * ---------------
 *   • Ribbon is now `position: fixed` (z=60, above the AppShell mobile
 *     header at z=30) and publishes its measured height as a CSS
 *     variable `--app-ribbon-h` on `<html>`. AppShell's fixed mobile
 *     header + main padding read that variable to shift below the
 *     ribbon — fixes the "ribbon hidden behind top bar after login".
 *   • Prominent inline Play Store / App Store badge on the right —
 *     official-style pill with the platform logo, "GET IT ON /
 *     DOWNLOAD ON" label and store name. "For a better experience"
 *     copy on the left.
 *
 * Behaviour
 * ---------
 *   • Runs `/api/app-links` on mount.
 *   • Detects platform from `navigator.userAgent`.
 *   • Renders only when the matching URL is non-empty AND on that
 *     platform's browser. Hidden inside the native app itself
 *     (`window.Capacitor.isNativePlatform()`).
 *   • Dismissal is `sessionStorage`-scoped so the visitor gets a break
 *     within the session but sees the nudge again on the next visit.
 */
const DISMISS_KEY = "app_download_ribbon_dismissed";
const CSS_VAR = "--app-ribbon-h";

function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/macintosh/i.test(ua) && typeof document !== "undefined" &&
      "ontouchend" in document) return "ios";
  return "other";
}

/** Google Play multicolour play-triangle logo (official brand colours). */
function GooglePlayIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 40 44" className={className} aria-hidden="true">
      <path d="M2 2.6C1.4 3.1 1 4 1 5.2v33.6c0 1.2.4 2.1 1 2.6l19.4-19.4L2 2.6z" fill="#00C3F0" />
      <path d="M28.4 15.5L22.3 22l6.1 6.5 7.2-4.1c2-1.2 2-4.1 0-5.3l-7.2-3.6z" fill="#FFCE00" />
      <path d="M2 2.6c.9-.7 2.2-.7 3.3 0l22.1 12.5-6.1 6.9L2 2.6z" fill="#00E574" />
      <path d="M2 41.4c.9.7 2.2.7 3.3 0l22.1-13-6.1-6-19.3 19z" fill="#FF3B30" />
    </svg>
  );
}

/** Simple Apple mark for the App Store badge. */
function AppleIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 384 512" className={className} aria-hidden="true" fill="currentColor">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

/** Official-style store pill badge. */
function StoreBadge({ platform }) {
  const isAndroid = platform === "android";
  const overline = isAndroid ? "GET IT ON" : "DOWNLOAD ON";
  const store = isAndroid ? "Google Play" : "App Store";
  return (
    <span
      className="inline-flex items-center gap-2 border-2 border-white bg-white text-ink px-3 py-1.5 font-bold shadow-brutalSm shrink-0"
      data-testid={`app-download-badge-${platform}`}
    >
      {isAndroid ? <GooglePlayIcon /> : <AppleIcon className="w-5 h-5" />}
      <span className="text-left leading-none">
        <span className="block text-[8px] font-black uppercase tracking-widest text-ink/60">{overline}</span>
        <span className="block text-sm font-black tracking-tight -mt-0.5">{store}</span>
      </span>
    </span>
  );
}

export default function AppDownloadRibbon() {
  const [links, setLinks] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; }
    catch { return false; }
  });
  const platform = detectPlatform();
  const shownTrackedRef = useRef(false);
  const nodeRef = useRef(null);
  const { user } = useAuth();
  const { pathname } = useLocation();

  // v146.38 — The download ribbon is a nudge for people using the web
  // app dashboard, not for anonymous visitors on someone's bio/action
  // page. Hide it on:
  //   (a) any public bio hub or action page under /u/*
  //   (b) sessions with no logged-in user (guest visitors on landing,
  //       auth screens, etc.)
  // Logged-in creators viewing the web dashboard still see the ribbon
  // on their own routes (/app/*, /admin/*, /u2/*, ...).
  const isPublicBioRoute = pathname === "/u" || pathname.startsWith("/u/");
  const isAnonymous = !user;
  const hiddenByRoute = isPublicBioRoute || isAnonymous;

  useEffect(() => {
    if (hiddenByRoute) return;
    if (platform === "other" || platform === "unknown") return;
    if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/app-links");
        if (alive) setLinks(data || {});
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [platform, hiddenByRoute]);

  // v145.17 — Publish the ribbon's measured height so AppShell can push
  // its fixed mobile header + main padding below us. Cleanup on unmount
  // (dismissed) resets the var so the layout collapses back.
  useLayoutEffect(() => {
    const el = nodeRef.current;
    if (!el || typeof document === "undefined") return undefined;
    const set = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty(CSS_VAR, `${h}px`);
    };
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty(CSS_VAR);
    };
  });

  if (hiddenByRoute) return null;
  if (dismissed) return null;
  if (platform === "other" || platform === "unknown") return null;
  if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) return null;
  if (!links) return null;

  const url = platform === "android" ? links.android_url : links.ios_url;
  if (!url) return null;

  if (!shownTrackedRef.current) {
    shownTrackedRef.current = true;
    trackSiteEvent("app_ribbon_shown", { platform });
  }

  const handleDismiss = (e) => {
    e.preventDefault(); e.stopPropagation();
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    document.documentElement.style.removeProperty(CSS_VAR);
    setDismissed(true);
  };

  const handleClick = () => {
    trackSiteEvent("app_ribbon_click", { platform });
  };

  return (
    <a
      ref={nodeRef}
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={externalLinkClickHandler(url, handleClick)}
      data-testid="app-download-ribbon"
      data-platform={platform}
      className="fixed top-0 inset-x-0 z-[60] block bg-ink text-white border-b-2 border-ink pt-[env(safe-area-inset-top,0)]"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 flex items-center gap-3">
        {/* Copy — hides some text on tiny screens so the badge stays prominent. */}
        <div className="flex-1 min-w-0">
          <p className="text-[11px] sm:text-sm font-bold leading-tight">
            <span className="block sm:inline">
              <span className="border-2 border-white bg-coral px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest mr-1.5 align-middle">
                Tip
              </span>
              For a better experience,
            </span>
            <span className="hidden sm:inline">{" "}</span>
            <span className="block sm:inline text-white/80 font-medium">
              install the Linkola app.
            </span>
          </p>
        </div>
        {/* Prominent store badge — this is the "download button". */}
        <StoreBadge platform={platform} />
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss download prompt"
          data-testid="app-download-ribbon-dismiss"
          className="shrink-0 text-white/60 hover:text-white leading-none p-1 -mr-1"
        >
          <X className="w-4 h-4" strokeWidth={3} />
        </button>
      </div>
    </a>
  );
}
